import { ButtonTertiary, Colors, Icon, Spinner } from '@amelio/ui-library';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
    CommunicationBlockId,
    getCheckedEvaluationTypes,
    getVisibleCommunicationBlocks,
} from './communicationBlocks';
import { getFirstInvalidCommunicationError } from './communicationValidation';
import { Communications } from './components/Communications/Communications';
import { useAutosaveCommunicationSwitches } from './useAutosaveCommunicationSwitches';
import { useCommunicationBlockViewModels } from './useCommunicationBlockViewModels';
import { useCommunicationCardsState } from './useCommunicationCardsState';
import { useCommunicationPersistence } from './useCommunicationPersistence';
import { useCommunicationTestEmail } from './useCommunicationTestEmail';

import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationGroupFormNavigatorParams } from '@/navigation/CycleEvaluation/CycleEvaluationGroupFormNavigator';

import { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import { useGetCycleEvaluationGroup } from '@/api/cycle-evaluation-services/cycle-evaluation-group';
import { communicationsOnNextAtom } from '@/atoms/cycleEvaluation';
import { userInstanceAtom } from '@/atoms/userDetails';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations, useNavigate, useRegisterPageAction } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useError } from '@/hooks/utils/useError';
import { isCycleEvaluationGroupLaunched } from '@/navigation/CycleEvaluation/cycleEvaluationGroupLaunched';
import { isCycleEvaluationGroupReadOnly } from '@/navigation/CycleEvaluation/cycleEvaluationGroupReadOnly';
import { findGroupEvaluationByType } from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';
import { StepFooter } from '@/navigation/shared/StepFooter';
import { RouteName } from '@/navigation/types';
import i18n from '@/plugins/i18n';
import { showWarningToast } from '@/plugins/Toast';
import { getAvailableLanguages, normalizeLanguageCode } from '@/utils/localization';

/**
 * Step of the cycle configuration flow: one block per DISPLAYED milestone, each carrying its
 * own enable toggle and up to two email cards.
 *
 * This step's local card state (`cards`, one entry per block+audience, still plain
 * `useState` rather than `react-hook-form` — the scaffolding shape, left as is) is what
 * `onNext` sends to the group's own PUT contract's optional `communications` field. This
 * is the ONLY step allowed to set that key — every other step's own save must leave it `undefined`,
 * or it would silently wipe this step's work
 * (`CycleEvaluationGroupRequest.Communications`, "ABSENT MEANS LEAVE IT ALONE"). Hydration mirrors
 * this: a block or email the group already carries is read back verbatim for the language it holds;
 * only a language nobody has ever saved falls back to the template default
 * (`resolveCommunicationEmailContent`).
 */
export const CommunicationsPage = () => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const { cycleEvaluationGroupId } = useParams<CycleEvaluationGroupFormNavigatorParams>();

    const {
        data: group,
        isError: isGroupError,
        isPending: isGroupPending,
    } = useGetCycleEvaluationGroup(cycleEvaluationGroupId);

    const selfEvaluationChild = findGroupEvaluationByType(group, EvaluationType.SELF_EVALUATION);
    const feedback360Child = findGroupEvaluationByType(group, EvaluationType.EVALUATION_360);
    const managerEvaluationChild = findGroupEvaluationByType(
        group,
        EvaluationType.MANAGER_EVALUATION,
    );
    const selfEvaluationQuery = useGetCycleEvaluation(selfEvaluationChild?.id ?? '');
    const feedback360Query = useGetCycleEvaluation(feedback360Child?.id ?? '');
    const managerEvaluationQuery = useGetCycleEvaluation(managerEvaluationChild?.id ?? '');

    const legacyScheduleQueryByEvaluationType: Partial<
        Record<EvaluationTypeValue, typeof selfEvaluationQuery>
    > = useMemo(
        () => ({
            [EvaluationType.SELF_EVALUATION]: selfEvaluationQuery,
            [EvaluationType.EVALUATION_360]: feedback360Query,
            [EvaluationType.MANAGER_EVALUATION]: managerEvaluationQuery,
        }),
        [selfEvaluationQuery, feedback360Query, managerEvaluationQuery],
    );

    const currentUser = useAtomValue(userInstanceAtom);
    const defaultLanguage = useMemo(() => normalizeLanguageCode(i18n.language), []);
    const availableLanguageCodes = useMemo(() => Object.keys(getAvailableLanguages()), []);

    const checkedTypes = useMemo(() => getCheckedEvaluationTypes(group), [group]);
    const visibleBlocks = useMemo(
        () => getVisibleCommunicationBlocks(checkedTypes),
        [checkedTypes],
    );

    const legacyScheduleQueries = checkedTypes
        .map((type) => legacyScheduleQueryByEvaluationType[type])
        .filter((query) => query !== undefined);

    // A refusal ends the wait: that child will never produce data, and going on waiting leaves the
    // skeleton up forever with nothing but the error handler to speak for it.
    const isLegacyScheduleDataReady = legacyScheduleQueries.every(
        (query) => !!query.data || !!query.isError,
    );

    const isReadOnly = isCycleEvaluationGroupReadOnly(group);
    const isLaunched = isCycleEvaluationGroupLaunched(group);

    const {
        blockEnabled,
        setBlockEnabled,
        cards,
        updateCard,
        updateCardText,
        hydratedRef,
        hasUnsavedSwitchChanges,
        hasUserEdited,
        markSwitchesSaved,
    } = useCommunicationCardsState({
        group,
        visibleBlocks,
        isLegacyScheduleDataReady,
        legacyScheduleQueryByEvaluationType,
        defaultLanguage,
        availableLanguageCodes,
        currentUserEmail: currentUser.email,
        t,
    });

    const { buildOnTestEmail, buildOnGroupTestEmail, isSendingTestEmail, isSendingGroupTestEmail } =
        useCommunicationTestEmail({ cards, cycleEvaluationGroupId, t, handleError });

    const {
        persistCommunications,
        onCardLanguageChange,
        isPersisting,
        isPersistingRef,
        lastWrittenSwitchesSignatureRef,
    } = useCommunicationPersistence({
        group,
        cycleEvaluationGroupId,
        visibleBlocks,
        blockEnabled,
        cards,
        legacyScheduleQueryByEvaluationType,
        hydratedRef,
        isReadOnly,
        isUntouchedLaunchedCycle: isLaunched && !hasUserEdited,
        defaultLanguage,
        updateCard,
        handleError,
    });

    useAutosaveCommunicationSwitches({
        isReadOnly,
        hasUnsavedSwitchChanges,
        cycleEvaluationGroupId,
        hydratedRef,
        visibleBlocks,
        group,
        legacyScheduleQueryByEvaluationType,
        blockEnabled,
        cards,
        isPersisting,
        isPersistingRef,
        lastWrittenSwitchesSignatureRef,
        persistCommunications,
        onAutosaved: markSwitchesSaved,
    });

    const { blocks } = useCommunicationBlockViewModels({
        visibleBlocks,
        group,
        cards,
        blockEnabled,
        setBlockEnabled,
        cycleEvaluationGroupId,
        defaultLanguage,
        t,
        updateCard,
        updateCardText,
        onCardLanguageChange,
        buildOnTestEmail,
        buildOnGroupTestEmail,
        isSendingTestEmail,
        isSendingGroupTestEmail,
        isReadOnly,
    });

    const firstInvalidCommunicationError = useCallback(
        () => getFirstInvalidCommunicationError({ visibleBlocks, blockEnabled, cards }),
        [visibleBlocks, blockEnabled, cards],
    );

    const onNext = useCallback(async () => {
        // A launched cycle whose emails were never touched has nothing to validate or write:
        // "Next" merely moves on, the same as any other untouched step. A completed cycle is not
        // that case — its save keeps refusing, as every step's read-only backstop does.
        if (!isReadOnly && isLaunched && !hasUserEdited) {
            return true;
        }

        const validationError = firstInvalidCommunicationError();

        if (validationError) {
            showWarningToast({
                title: t('errors.genericTitle'),
                message: t(validationError),
            });

            return false;
        }

        const isSaved = await persistCommunications();

        if (isSaved) {
            markSwitchesSaved();
        }

        return isSaved;
    }, [
        isReadOnly,
        isLaunched,
        hasUserEdited,
        firstInvalidCommunicationError,
        persistCommunications,
        markSwitchesSaved,
        t,
    ]);

    useRegisterPageAction(communicationsOnNextAtom, onNext);

    useFetchErrorsHandler({
        errors: [isGroupError, ...legacyScheduleQueries.map((query) => !!query.isError)],
    });

    const navigate = useNavigate();
    const [isNavigatingToSummary, setIsNavigatingToSummary] = useState(false);

    const handleContinueToLaunchSummary = useCallback(async () => {
        if (!cycleEvaluationGroupId) {
            return;
        }

        setIsNavigatingToSummary(true);

        try {
            if (!isReadOnly) {
                const canProceed = await onNext();

                if (!canProceed) {
                    return;
                }
            }

            navigate({
                path: `${RouteName.CycleEvaluationGroupLaunchSummary}/${cycleEvaluationGroupId}`,
            });
        } finally {
            setIsNavigatingToSummary(false);
        }
    }, [cycleEvaluationGroupId, onNext, navigate, isReadOnly]);

    return (
        <Communications
            isLoading={isGroupPending || !hydratedRef.current}
            blocks={blocks}
            footer={
                <div className={'flex items-start justify-end gap-2'}>
                    <StepFooter />

                    <ButtonTertiary
                        className={'mt-6 min-w-40 bg-white'}
                        dataTestId={'communications-continue-button'}
                        variant={'outlined'}
                        onClick={handleContinueToLaunchSummary}
                        disabled={isNavigatingToSummary}
                        rightIcon={
                            isNavigatingToSummary ? null : (
                                <Icon
                                    name={'arrow-arrow-right'}
                                    className={'ml-2'}
                                    color={Colors.PRIMARY}
                                />
                            )
                        }
                    >
                        {isNavigatingToSummary ? (
                            <Spinner
                                size={'sm'}
                                color={Colors.PRIMARY}
                            />
                        ) : (
                            t('common.next')
                        )}
                    </ButtonTertiary>
                </div>
            }
        />
    );
};

export { CommunicationBlockId };
